import { useState, useCallback } from 'react';
import { TutorialStep } from './InteractiveTutorial';

interface UseTutorialProps {
  steps: TutorialStep[];
  onComplete?: () => void;
  onSkip?: () => void;
}

export const useTutorial = ({
  steps,
  onComplete,
  onSkip,
}: UseTutorialProps) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const startTutorial = useCallback(() => {
    // Ensure clean reset before starting
    setIsActive(false);
    setCurrentStepIndex(0);
    // Start tutorial with a brief delay to ensure state is reset
    setTimeout(() => {
      setIsActive(true);
      setCurrentStepIndex(0);
    }, 50);
  }, []);

  const completeTutorial = useCallback(() => {
    // Close tutorial when user completes it
    setIsActive(false);
    setCurrentStepIndex(0);
    onComplete?.();
  }, [onComplete]);

  const skipTutorial = useCallback(() => {
    // Close tutorial when user clicks X button
    setIsActive(false);
    setCurrentStepIndex(0);
    onSkip?.();
  }, [onSkip]);

  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      completeTutorial();
    }
  }, [currentStepIndex, steps.length, completeTutorial]);

  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < steps.length) {
        setCurrentStepIndex(stepIndex);
      }
    },
    [steps.length]
  );

  return {
    isActive,
    currentStepIndex,
    startTutorial,
    completeTutorial,
    skipTutorial,
    nextStep,
    previousStep,
    goToStep,
  };
};
